<template>
  <nav
    data-site-nav
    :class="[
      isHome ? 'fixed' : 'relative',
      'w-full z-50 transition-all duration-300 py-2',
      hasShadow ? 'bg-white shadow-md' : 'bg-transparent shadow-none',
    ]"
  >
    <div class="container mx-auto px-4 md:px-6">
      <div data-site-nav-row class="flex justify-between items-center gap-3">
        <RouterLink
          :to="navRoutes[0]"
          class="flex items-center align-center min-w-0"
        >
          <img
            src="/Group.png"
            alt="Borówka Amerykańska Logo"
            class="h-12 md:h-14"
          />
          <span
            class="hidden md:block text-base lg:text-2xl xl:text-3xl font-bold text-navy leading-tight ml-2 lg:ml-3 animate-fade-in max-w-[170px] lg:max-w-none truncate lg:overflow-visible"
          >
            <span class="text-blueberry">BorowkaSzczecin</span>
          </span>
        </RouterLink>

        <div class="hidden lg:flex lg:space-x-8 items-center">
          <RouterLink
            v-for="route in navRoutes"
            :to="route"
            :key="route.hash"
            class="text-navy hover:text-blueberry font-medium transition-colors text-base whitespace-nowrap"
          >
            {{ t(route.labelKey) }}
          </RouterLink>
          <OrderGateLink
            class="btn-primary lg:py-3 lg:px-6 lg:text-base whitespace-nowrap"
          >
            {{ t(orderRoute.labelKey) }}
          </OrderGateLink>

          <div class="flex items-center gap-1 ml-1 lg:ml-2">
            <button
              v-for="lang in ['pl', 'en'] as const"
              :key="lang"
              @click="setLocale(lang)"
              :class="[
                'text-sm font-semibold uppercase px-1.5 py-0.5 rounded transition-colors',
                locale === lang
                  ? 'text-blueberry'
                  : 'text-muted-foreground/70 hover:text-navy',
              ]"
            >
              {{ lang }}
            </button>
          </div>
        </div>

        <div class="lg:hidden flex items-center">
          <button
            class="text-navy p-2"
            :aria-label="isMenuOpen ? 'Zamknij menu' : 'Otwórz menu'"
            @click="isMenuOpen = !isMenuOpen"
          >
            <X v-if="isMenuOpen" :size="24" />
            <Menu v-else :size="24" />
          </button>
        </div>
      </div>

      <!-- Mobile menu -->
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <div v-if="isMenuOpen" class="lg:hidden mt-2 pb-4">
          <div
            class="flex flex-col rounded-xl border border-border bg-white shadow-lg overflow-hidden"
          >
            <RouterLink
              v-for="route in navRoutes"
              :to="route"
              :key="route.hash"
              class="flex items-center gap-3 text-navy hover:text-blueberry hover:bg-blueberry-lighter/10 font-medium transition-colors px-5 py-3.5 border-b border-border/50 last:border-b-0"
              @click="isMenuOpen = false"
            >
              {{ t(route.labelKey) }}
            </RouterLink>
            <div class="px-4 py-3 bg-secondary/50 flex flex-col gap-3">
              <OrderGateLink
                class="btn-primary block w-full text-center"
                @handled="isMenuOpen = false"
              >
                {{ t(orderRoute.labelKey) }}
              </OrderGateLink>
              <div class="flex items-center justify-center gap-2">
                <button
                  v-for="lang in ['pl', 'en'] as const"
                  :key="lang"
                  @click="setLocale(lang)"
                  :class="[
                    'text-sm font-semibold uppercase px-3 py-1 rounded-md transition-colors border',
                    locale === lang
                      ? 'text-blueberry border-blueberry bg-blueberry/5'
                      : 'text-muted-foreground border-border hover:text-navy hover:border-navy',
                  ]"
                >
                  {{ lang }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { X, Menu } from "lucide-vue-next";
import { useWindowScroll } from "@vueuse/core";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { navRoutes, orderRoute } from "@/router/navigation";
import OrderGateLink from "@/components/OrderGateLink.vue";

const { y } = useWindowScroll();
const isMenuOpen = ref(false);
const { t, locale } = useI18n();

const isScrolled = computed(() => y.value > 10);
const route = useRoute();
const isHome = computed(() => route.path === "/" || route.name === "home");
const hasShadow = computed(() => {
  if (isHome.value) return isScrolled.value;
  return true;
});

function setLocale(lang: "pl" | "en") {
  locale.value = lang;
  localStorage.setItem("locale", lang);
  document.cookie = `locale=${lang}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
}
</script>
